import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Upload, FileText, Bell, MessageSquare, Shield, 
  Search, Filter, ChevronDown, Plus, Trash2, Edit, 
  CheckCircle, XCircle, LogOut, Settings, BarChart3, Target, Award, Flame, BookOpen, Trophy, Calendar, Palette,
  ChevronLeft, Loader2, Briefcase, TrendingUp, Star, Heart, X, FileDown, BellOff, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import ResourceManagement from '@/components/ResourceManagement';
import ClientOnboardingWizard from '@/components/ClientOnboardingWizard';
import NotificationBell from '@/components/NotificationBell';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const YAS_LOGO = '/yas-logo.png';

const AdminDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [activeTab, setActiveTab] = useState('analytics');
  const [users, setUsers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [moderationQueue, setModerationQueue] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [projectDeadline, setProjectDeadline] = useState('');
  const [savingDeadline, setSavingDeadline] = useState(false);
  
  // NEW: Consent tracking for Program Admin
  const [consentData, setConsentData] = useState(null);
  const [bulkResending, setBulkResending] = useState(false);
  
  // Modal states
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [showBulkFAQ, setShowBulkFAQ] = useState(false);
  const [bulkFAQText, setBulkFAQText] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResults, setImportResults] = useState(null);
  
  // Form states
  const [announcementForm, setAnnouncementForm] = useState({ title: '', content: '', category: 'general' });
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: 'general' });

  useEffect(() => {
    fetchAllData();
    fetchProjectDeadline();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProjectDeadline = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/settings/project-deadline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.deadline) {
          // Convert to datetime-local format
          setProjectDeadline(data.deadline.slice(0, 16));
        }
      }
    } catch (error) {
      console.error('Error fetching deadline:', error);
    }
  };

  const saveProjectDeadline = async () => {
    if (!projectDeadline) {
      toast.error('Please select a deadline date and time');
      return;
    }
    
    setSavingDeadline(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/settings/project-deadline?deadline=${encodeURIComponent(projectDeadline)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Project deadline saved!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save deadline');
    } finally {
      setSavingDeadline(false);
    }
  };

  const fetchAllData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [usersRes, announcementsRes, faqsRes, moderationRes, analyticsRes, overviewRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/users?limit=1200`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/announcements`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/faq`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/moderation/queue`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/coach/analytics`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/gps/admin-overview`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }
      if (announcementsRes.ok) setAnnouncements(await announcementsRes.json());
      if (faqsRes.ok) setFaqs(await faqsRes.json());
      if (moderationRes.ok) setModerationQueue(await moderationRes.json());
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (overviewRes.ok) setOverviewData(await overviewRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
    // Fetch consent data in parallel
    fetchConsentStatus();
  };

  const fetchConsentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/staff/consent-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setConsentData(await res.json());
      }
    } catch (error) {
      console.error('Error fetching consent status:', error);
    }
  };
  
  const resendAllPendingConsents = async () => {
    if (!consentData || consentData.pending === 0) return;
    
    if (!confirm(`Send consent reminders to ${consentData.pending} parents?`)) return;
    
    setBulkResending(true);
    try {
      const pendingYouth = consentData.youth_details.filter(y => y.consent_status === 'pending');
      
      for (const youth of pendingYouth) {
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/api/staff/resend-consent/${youth.id}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success(`Sent ${pendingYouth.length} consent reminders!`);
      fetchConsentStatus();
    } catch (error) {
      toast.error('Error sending bulk reminders');
    } finally {
      setBulkResending(false);
    }
  };
  
  const getConsentStatusBadge = (youthId) => {
    if (!consentData) return null;
    const youth = consentData.youth_details?.find(y => y.id === youthId);
    if (!youth || !youth.is_minor) return null;
    
    return youth.consent_status === 'pending' ? (
      <Badge className="bg-yellow-500/20 text-yellow-700 font-semibold text-xs">
        🟡 Pending
      </Badge>
    ) : (
      <Badge className="bg-green-500/20 text-green-700 font-semibold text-xs">
        ✅ Consented
      </Badge>
    );
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/admin/import-users`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });

      if (res.ok) {
        const results = await res.json();
        setImportResults(results);
        toast.success(`Imported ${results.success.length} users successfully`);
        fetchAllData();
      } else {
        toast.error('Import failed');
      }
    } catch (error) {
      toast.error('Import error');
    }
  };

  const createAnnouncement = async () => {
    try {
      const res = await fetch(`${API_URL}/api/announcements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title: announcementForm.title,
          content: announcementForm.content,
          category: announcementForm.category,
          target_roles: ['youth']
        })
      });

      if (res.ok) {
        toast.success('Announcement created and sent! 📢');
        setShowAnnouncementModal(false);
        setAnnouncementForm({ title: '', content: '', category: 'general' });
        fetchAllData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create announcement');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to create announcement');
    }
  };

  const createFAQ = async () => {
    try {
      const res = await fetch(`${API_URL}/api/faq`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          question: faqForm.question,
          answer: faqForm.answer,
          category: faqForm.category
        })
      });

      if (res.ok) {
        toast.success('FAQ added! ❓');
        setShowFAQModal(false);
        setFaqForm({ question: '', answer: '', category: 'general' });
        fetchAllData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create FAQ');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to create FAQ');
    }
  };

  const moderatePost = async (postId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/moderation/${postId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ action })
      });

      if (res.ok) {
        toast.success(`Post ${action}d`);
        fetchAllData();
      }
    } catch (error) {
      toast.error('Moderation failed');
    }
  };

  const filteredUsers = users.filter(u => {
    if (u.role === 'platform_admin') return false;
    const matchesSearch = u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    const matchesGroup = groupFilter === 'all' || u.program_type === groupFilter;
    return matchesSearch && matchesRole && matchesGroup;
  });

  const visibleUsers = users.filter(u => u.role !== 'platform_admin');
  const nextgenCount = users.filter(u => u.role === 'youth' && u.program_type === 'nextgen').length;
  const upnextCount = users.filter(u => u.role === 'youth' && u.program_type === 'upnext').length;
  const stats = {
    totalUsers: visibleUsers.length,
    youth: visibleUsers.filter(u => u.role === 'youth').length,
    leadMonitors: visibleUsers.filter(u => u.role === 'lead_monitor').length,
    parents: visibleUsers.filter(u => u.role === 'parent').length,
    pendingModeration: moderationQueue.length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-2xl font-heading font-bold text-primary">Loading admin panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="p-6 text-white" style={{ background: colors.header_gradient || colors.header }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img src={YAS_LOGO} alt="SYEP" className="w-20 h-20 object-contain bg-white rounded-xl p-2 shadow-md" />
              <div>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>Admin Dashboard</p>
                <h1 className="font-heading text-2xl font-bold" style={{ color: 'white' }}>Admin Dashboard</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => navigate('/help-guides')} variant="ghost" className="text-white hover:bg-white/10">
                <Palette className="w-5 h-5 mr-2" /> Help Guides
              </Button>
              <div className="flex items-center gap-2">
                <NotificationBell />
                <Button onClick={handleLogout} variant="ghost" className="text-white hover:bg-white/10">
                  <LogOut className="w-5 h-5 mr-2" /> Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-6 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total Users', value: stats.totalUsers, icon: Users, color: 'bg-blue-500', filter: 'all' },
            { label: 'Youth', value: stats.youth, icon: Users, color: 'bg-green-500', filter: 'youth' },
            { label: 'Lead Monitors', value: stats.leadMonitors, icon: Shield, color: 'bg-purple-500', filter: 'lead_monitor' },
            { label: 'Parents', value: stats.parents, icon: Users, color: 'bg-orange-500', filter: 'parent' },
            { label: 'Pending Review', value: stats.pendingModeration, icon: FileText, color: 'bg-red-500', filter: 'moderation' }
          ].map((stat, idx) => (
            <Card 
              key={idx} 
              className="p-4 bg-white shadow-lg rounded-2xl cursor-pointer hover:shadow-xl transition-all hover:scale-105"
              onClick={() => {
                if (stat.filter === 'moderation') {
                  setActiveTab('moderation');
                } else if (stat.filter) {
                  setRoleFilter(stat.filter);
                  setActiveTab('users');
                } else {
                  setRoleFilter('all');
                  setActiveTab('users');
                }
              }}
              data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${stat.color} rounded-xl flex items-center justify-center`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Onboarding Wizard for new admins */}
        <ClientOnboardingWizard user={user} onComplete={() => {}} />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white rounded-2xl p-1 shadow-sm mb-6 overflow-x-auto flex-nowrap w-full justify-start" style={{ scrollbarWidth: 'none' }}>
            <TabsTrigger value="analytics" className="rounded-xl flex-shrink-0 whitespace-nowrap">
              <BarChart3 className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="reports" className="rounded-xl flex-shrink-0 whitespace-nowrap">
              <FileText className="w-4 h-4 mr-2" /> Reports
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-xl flex-shrink-0 whitespace-nowrap">
              <Users className="w-4 h-4 mr-2" /> Youth
            </TabsTrigger>
            <TabsTrigger value="staff" className="rounded-xl flex-shrink-0 whitespace-nowrap">
              <Shield className="w-4 h-4 mr-2" /> Staff
            </TabsTrigger>
            <TabsTrigger value="announcements" className="rounded-xl flex-shrink-0 whitespace-nowrap">
              <Bell className="w-4 h-4 mr-2" /> Announcements
            </TabsTrigger>
            <TabsTrigger value="parents" className="rounded-xl flex-shrink-0 whitespace-nowrap">
              <Heart className="w-4 h-4 mr-2" /> Parents
            </TabsTrigger>
            <TabsTrigger value="resources" className="rounded-xl flex-shrink-0 whitespace-nowrap">
              <FileText className="w-4 h-4 mr-2" /> Resources
            </TabsTrigger>
            <TabsTrigger value="faq" className="rounded-xl flex-shrink-0 whitespace-nowrap">
              <MessageSquare className="w-4 h-4 mr-2" /> FAQ
            </TabsTrigger>
            <TabsTrigger value="moderation" className="rounded-xl flex-shrink-0 whitespace-nowrap">
              <Shield className="w-4 h-4 mr-2" /> Moderation
              {stats.pendingModeration > 0 && (
                <Badge className="ml-2 bg-red-500">{stats.pendingModeration}</Badge>
              )}
            </TabsTrigger>
            {stats.parents > 0 && (
              <TabsTrigger value="consent" className="rounded-xl flex-shrink-0 whitespace-nowrap">
                <CheckCircle className="w-4 h-4 mr-2" /> Youth Parent Consent
              </TabsTrigger>
            )}
            <TabsTrigger value="settings" className="rounded-xl flex-shrink-0 whitespace-nowrap">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {/* NEW: Program Overview Dashboard */}
            {overviewData ? (() => {
              const k = overviewData.kpis || {};
              return (
                <div className="space-y-5">
                  {/* KPI Cards — CLICKABLE */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                      { label: 'Total Youth', val: k.total_youth, sub: `${k.active_this_week} active this week`, color: 'blue', alert: k.inactive_7_days > 0 ? `${k.inactive_7_days} inactive` : null, tab: 'users' },
                      { label: 'Goals', val: `${k.completed_goals}/${k.total_goals}`, sub: `${k.goal_rate}% completion rate`, color: 'emerald', tab: 'reports' },
                      { label: 'Courses', val: k.total_workshops, sub: 'completed by youth', color: 'violet', tab: 'reports' },
                      { label: 'Careers Explored', val: k.total_careers, sub: 'by all youth', color: 'amber', tab: 'reports' },
                      { label: 'Check-ins', val: k.total_checkins, sub: 'submitted total', color: 'green', tab: 'reports' },
                      { label: 'Wins Posted', val: k.total_wins, sub: 'on the Wins Wall', color: 'orange', action: () => navigate('/wins') },
                      { label: 'Avg Points', val: k.avg_points, sub: 'per youth', color: 'purple', tab: 'users' },
                      { label: 'Youth Parent Consent', val: `${k.consent_signed}/${k.consent_signed + k.consent_pending}`, sub: k.consent_pending > 0 ? `${k.consent_pending} pending` : 'all signed', color: k.consent_pending > 0 ? 'red' : 'green', tab: 'consent' },
                    ].map(kpi => (
                      <Card key={kpi.label} className="p-4 rounded-xl cursor-pointer hover:shadow-md transition-all hover:ring-1 hover:ring-orange-200"
                        onClick={() => kpi.action ? kpi.action() : setActiveTab(kpi.tab)} data-testid={`kpi-${kpi.label.toLowerCase().replace(/ /g, '-')}`}>
                        <p className="text-2xl font-bold">{kpi.val}</p>
                        <p className="text-sm font-semibold text-gray-900">{kpi.label}</p>
                        <p className="text-xs text-muted-foreground">{kpi.sub}</p>
                        {kpi.alert && <p className="text-xs text-red-500 font-semibold mt-1">{kpi.alert}</p>}
                      </Card>
                    ))}
                  </div>

                  {/* Needs Attention — CLICKABLE */}
                  {(k.unanswered_helpdesk > 0 || k.unanswered_parent_msgs > 0) && (
                    <Card className="p-4 rounded-xl bg-red-50 border border-red-200 cursor-pointer hover:bg-red-100 transition-all"
                      onClick={() => navigate('/admin')}>
                      <h3 className="font-bold text-sm text-red-800 mb-2">Needs Attention</h3>
                      {k.unanswered_helpdesk > 0 && <p className="text-sm text-red-700">{k.unanswered_helpdesk} unanswered questions from youth — click to view in Staff Dashboard</p>}
                      {k.unanswered_parent_msgs > 0 && <p className="text-sm text-red-700">{k.unanswered_parent_msgs} unanswered parent messages</p>}
                    </Card>
                  )}

                  <div className="grid lg:grid-cols-2 gap-5">
                    {/* AI Insights — Youth who need support */}
                    <Card className="p-5 rounded-2xl" style={{ background: 'linear-gradient(135deg, #EDE9FE 0%, #FEF3C7 100%)' }}>
                      <h3 className="font-bold text-sm text-violet-900 mb-1 flex items-center gap-2"><Target className="w-4 h-4" /> AI Insights</h3>
                      <p className="text-[10px] text-violet-600 mb-3">AI-detected alerts and celebrations about your youth</p>
                      <div className="space-y-2">
                        {(overviewData.ai_alerts || []).map((a, i) => (
                          <div key={`a${i}`} className="p-2.5 rounded-lg bg-red-50 border border-red-100">
                            <p className="text-xs text-red-800">{a.message}</p>
                          </div>
                        ))}
                        {(overviewData.ai_celebrations || []).map((c, i) => (
                          <div key={`c${i}`} className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-100">
                            <p className="text-xs text-emerald-800">{c.message}</p>
                          </div>
                        ))}
                        {(overviewData.ai_alerts || []).length === 0 && (overviewData.ai_celebrations || []).length === 0 && (
                          <p className="text-xs text-violet-700 text-center py-2">All looking good!</p>
                        )}
                      </div>
                    </Card>

                    {/* Recent Activity Feed — Real-time log of actions */}
                    <Card className="p-5 rounded-2xl">
                      <h3 className="font-bold text-sm mb-1 flex items-center gap-2"><Bell className="w-4 h-4" /> Recent Activity</h3>
                      <p className="text-[10px] text-gray-400 mb-3">Real-time log of what youth are doing on the platform</p>
                      <div className="space-y-2">
                        {(overviewData.recent_activity || []).slice(0, 8).map((a, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-gray-50">
                            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${a.type === 'goal' ? 'bg-[#E86C3D]' : a.type === 'checkin' ? 'bg-blue-500' : 'bg-gray-400'}`} />
                            <div>
                              <p className="text-xs text-gray-800">{a.message}</p>
                              {a.time && <p className="text-[10px] text-gray-400">{new Date(a.time).toLocaleDateString()}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>

                  {/* Program Health Summary */}
                  <Card className="p-5 rounded-2xl">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Program Health</h3>
                      <div className="flex gap-2">
                        <Button 
                          variant="default"
                          size="sm"
                          className="bg-violet-600 hover:bg-violet-700 rounded-xl text-xs gap-1"
                          data-testid="completion-tracker-btn"
                          onClick={() => window.location.href = '/admin/completion-tracker'}
                        >
                          <CheckCircle className="w-3 h-3" /> Completion Tracker
                        </Button>
                        <Button 
                          variant="default"
                          size="sm"
                          className="bg-orange-500 hover:bg-orange-600 rounded-xl text-xs gap-1"
                          data-testid="send-weekly-digest-btn"
                        onClick={async () => {
                          try {
                            const res = await fetch(`${API_URL}/api/admin/send-weekly-digest`, {
                              method: 'POST',
                              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                            });
                            if (res.ok) {
                              const data = await res.json();
                              toast.success(`Weekly digest sent to ${data.sent} recipients!`);
                            } else {
                              toast.error('Failed to send digest');
                            }
                          } catch (e) { toast.error('Connection error'); }
                        }}
                      >
                        <Bell className="w-3 h-3" /> Send Weekly Digest
                      </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="p-3 bg-emerald-50 rounded-xl text-center cursor-pointer hover:bg-emerald-100" onClick={() => navigate('/coach')}>
                        <p className="text-xl font-bold text-emerald-700">{k.active_this_week || 0}</p><p className="text-xs text-emerald-600">Active This Week</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-xl text-center cursor-pointer hover:bg-red-100" onClick={() => navigate('/coach')}>
                        <p className="text-xl font-bold text-red-700">{k.inactive_7_days || 0}</p><p className="text-xs text-red-600">Inactive 7+ Days</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-xl text-center cursor-pointer hover:bg-blue-100" onClick={() => setActiveTab('staff')}>
                        <p className="text-xl font-bold text-blue-700">{k.total_staff || 0}</p><p className="text-xs text-blue-600">Staff Members</p>
                      </div>
                      <div className="p-3 bg-purple-50 rounded-xl text-center cursor-pointer hover:bg-purple-100" onClick={() => setActiveTab('parents')}>
                        <p className="text-xl font-bold text-purple-700">{k.total_parents || 0}</p><p className="text-xs text-purple-600">Parents</p>
                      </div>
                    </div>
                  </Card>
                </div>
              );
            })() : (
              <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /><p className="text-sm text-gray-400 mt-2">Loading overview...</p></div>
            )}
          </TabsContent>

          {/* Users Tab */}
          {/* Reports Tab - Program Director's Data Center */}
          <TabsContent value="reports">
            <ProgramReportsTab user={user} />
          </TabsContent>

          <TabsContent value="users">
            <YouthUsersTab users={users} allUsers={users} />
          </TabsContent>

          {/* Staff Tab */}
          <TabsContent value="staff">
            <StaffTab users={users} />
          </TabsContent>

          {/* Parents Tab */}
          <TabsContent value="parents">
            <ParentsTab users={users} />
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources">
            <ResourceManagement programId={user?.program_id} userRole="program_admin" />
          </TabsContent>

          {/* Consent Tracking Tab */}
          <TabsContent value="consent">
            <Card className="p-6 rounded-3xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-500" />
                  Parent Consent Tracking
                </h2>
                {consentData && consentData.pending > 0 && (
                  <Button
                    onClick={resendAllPendingConsents}
                    disabled={bulkResending}
                    className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl"
                    data-testid="resend-all-consent-btn"
                  >
                    {bulkResending ? 'Sending...' : `Resend All (${consentData.pending})`}
                  </Button>
                )}
              </div>

              {/* Consent Stats */}
              {consentData && (
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-green-50 rounded-xl text-center">
                    <p className="text-3xl font-bold text-green-700">{consentData.consented || consentData.completed || 0}</p>
                    <p className="text-sm text-green-600">Consented</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-xl text-center">
                    <p className="text-3xl font-bold text-amber-700">{consentData.pending || 0}</p>
                    <p className="text-sm text-amber-600">Pending</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl text-center">
                    <p className="text-3xl font-bold text-gray-700">{consentData.not_required || 0}</p>
                    <p className="text-sm text-gray-600">Not Required</p>
                  </div>
                </div>
              )}

              {/* Youth Consent List */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-semibold">Youth</th>
                      <th className="text-left p-3 font-semibold">Program</th>
                      <th className="text-left p-3 font-semibold">Parent Name</th>
                      <th className="text-left p-3 font-semibold">Parent Email</th>
                      <th className="text-left p-3 font-semibold">Status</th>
                      <th className="text-left p-3 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consentData?.youth_details?.map(y => (
                      <tr key={y.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{y.avatar_emoji || '😊'}</span>
                            <span className="font-medium">{y.name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">{user?.program_name || 'Program'}</td>
                        <td className="p-3 text-sm">{y.parent_name || '—'}</td>
                        <td className="p-3 text-sm text-muted-foreground">{y.parent_email || 'Not set'}</td>
                        <td className="p-3">
                          {y.consent_status === 'completed' ? (
                            <Badge className="bg-green-100 text-green-800">Consented</Badge>
                          ) : y.consent_status === 'pending' ? (
                            <Badge className="bg-amber-100 text-amber-800">Pending</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-600">Not Required</Badge>
                          )}
                        </td>
                        <td className="p-3">
                          {y.consent_status === 'pending' && (
                            <Button size="sm" variant="outline" className="text-xs rounded-lg"
                              onClick={async () => {
                                try {
                                  await fetch(`${API_URL}/api/staff/resend-consent/${y.id}`, {
                                    method: 'POST',
                                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                  });
                                  toast.success(`Reminder sent for ${y.name}`);
                                } catch (e) { toast.error('Failed to send'); }
                              }}
                            >
                              Resend
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {(!consentData?.youth_details || consentData.youth_details.length === 0) && (
                      <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No youth users in this program</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements">
            <Card className="p-6 rounded-3xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-heading text-xl font-bold">Announcements</h2>
                <Button onClick={() => setShowAnnouncementModal(true)} className="rounded-xl">
                  <Plus className="w-4 h-4 mr-2" /> New Announcement
                </Button>
              </div>

              <div className="space-y-4">
                {announcements.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No announcements yet</p>
                ) : (
                  announcements.map((a) => (
                    <div key={a.id} className="bg-gray-50 rounded-2xl p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={
                              a.category === 'urgent' ? 'bg-red-500' :
                              a.category === 'reminder' ? 'bg-yellow-500' : 'bg-blue-500'
                            }>{a.category}</Badge>
                            <h3 className="font-bold">{a.title}</h3>
                          </div>
                          <p className="text-muted-foreground">{a.content}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {a.acknowledged_by?.length || 0} acknowledgments · {a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}
                          </p>
                        </div>
                        <button onClick={async () => {
                          if (window.confirm('Delete this announcement?')) {
                            try {
                              await fetch(`${API_URL}/api/announcements/${a.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }});
                              toast.success('Announcement deleted');
                              fetchAllData();
                            } catch (e) { toast.error('Failed'); }
                          }
                        }} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-announcement-${a.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq">
            <Card className="p-6 rounded-3xl">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading text-xl font-bold">FAQ Management</h2>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowBulkFAQ(true)} className="rounded-xl text-xs" data-testid="bulk-faq-btn">
                    <Upload className="w-3 h-3 mr-1" /> Bulk Upload
                  </Button>
                  <Button onClick={() => setShowFAQModal(true)} className="rounded-xl text-xs">
                    <Plus className="w-4 h-4 mr-1" /> Add FAQ
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-400 mb-4">These are the common questions your youth will see in their Inbox → FAQ tab. Add answers here so they can find info without messaging you.</p>

              <div className="space-y-3">
                {faqs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No FAQs yet — add some common questions your participants will have</p>
                ) : (
                  faqs.map((faq) => (
                    <div key={faq.id} className="bg-gray-50 rounded-2xl p-4 flex items-start gap-3">
                      <div className="flex-1">
                        <h3 className="font-bold text-sm mb-1">{faq.question}</h3>
                        <p className="text-sm text-muted-foreground">{faq.answer}</p>
                        <Badge variant="outline" className="mt-2 text-[9px]">{faq.category}</Badge>
                      </div>
                      <button onClick={async () => {
                        await fetch(`${API_URL}/api/faq/${faq.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }});
                        toast.success('FAQ deleted');
                        fetchAllData();
                      }} className="text-red-400 hover:text-red-600 text-xs p-1" data-testid={`delete-faq-${faq.id}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Moderation Tab */}
          <TabsContent value="moderation">
            <Card className="p-6 rounded-3xl">
              <h2 className="font-heading text-xl font-bold mb-2">Content Moderation</h2>
              <p className="text-sm text-muted-foreground mb-6">Review and manage content posted by youth. You can approve, reject, or delete posts from the Win Wall, goals, and messages.</p>

              {/* Pending Queue */}
              <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-amber-500" /> Pending Review</h3>
              <div className="space-y-4 mb-6">
                {moderationQueue.length === 0 ? (
                  <div className="text-center py-8 bg-emerald-50 rounded-2xl">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-green-600">All caught up — no posts pending review</p>
                  </div>
                ) : (
                  moderationQueue.map((post) => (
                    <div key={post.id} className="bg-gray-50 rounded-2xl p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">{post.user_avatar}</span>
                          <div>
                            <p className="font-semibold">{post.user_name}</p>
                            <Badge variant="outline" className="mb-2">{post.post_type}</Badge>
                            <p className="text-muted-foreground">{post.content}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => moderatePost(post.id, 'approve')} className="bg-green-500 hover:bg-green-600 rounded-xl">
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => moderatePost(post.id, 'reject')} className="rounded-xl">
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid gap-6">
              {/* Program Settings - Editable */}
              <ProgramSettingsSection user={user} fetchAllData={fetchAllData} />
              
              {/* Survey Management */}
              <SurveyManagementSection user={user} />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Import Users from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Upload a CSV file with the following columns: name, email, role, work_site, phone, lead_monitor_email, parent_email, parent_name
            </p>
            <Input
              type="file"
              accept=".csv"
              onChange={handleCSVImport}
              className="rounded-xl"
            />
            {importResults && (
              <div className="bg-gray-50 rounded-xl p-4 max-h-60 overflow-y-auto">
                <p className="font-semibold text-green-600 mb-2">
                  ✓ {importResults.success?.length || 0} users imported successfully
                </p>
                {importResults.errors?.length > 0 && (
                  <div>
                    <p className="font-semibold text-red-600 mb-2">
                      ✗ {importResults.errors.length} errors
                    </p>
                    {importResults.errors.map((err, idx) => (
                      <p key={idx} className="text-sm text-red-500">
                        Row {err.row}: {err.error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Announcement Modal */}
      <Dialog open={showAnnouncementModal} onOpenChange={setShowAnnouncementModal}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl" style={{ color: colors.text }}>Create Announcement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Title"
              value={announcementForm.title}
              onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
              className="rounded-xl"
              style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
            />
            <Textarea
              placeholder="Content"
              value={announcementForm.content}
              onChange={(e) => setAnnouncementForm({...announcementForm, content: e.target.value})}
              className="rounded-xl min-h-[100px]"
              style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
            />
            <Select 
              value={announcementForm.category} 
              onValueChange={(v) => setAnnouncementForm({...announcementForm, category: v})}
            >
              <SelectTrigger className="rounded-xl" style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}>
                <SelectValue placeholder="Send To" />
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                <SelectItem value="all" style={{ color: colors.text }}>Everyone (All Youth + Staff)</SelectItem>
                <SelectItem value="nextgen" style={{ color: colors.text }}>NextGEN Youth Only</SelectItem>
                <SelectItem value="upnext" style={{ color: colors.text }}>UpNEXT Youth Only</SelectItem>
                <SelectItem value="staff" style={{ color: colors.text }}>Staff / Lead Monitors Only</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={createAnnouncement} className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold">
              Create Announcement
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* FAQ Modal */}
      <Dialog open={showFAQModal} onOpenChange={setShowFAQModal}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Add FAQ</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Question"
              value={faqForm.question}
              onChange={(e) => setFaqForm({...faqForm, question: e.target.value})}
              className="rounded-xl"
            />
            <Textarea
              placeholder="Answer"
              value={faqForm.answer}
              onChange={(e) => setFaqForm({...faqForm, answer: e.target.value})}
              className="rounded-xl min-h-[100px]"
            />
            <Input
              placeholder="Category (e.g., payroll, general, worksite)"
              value={faqForm.category}
              onChange={(e) => setFaqForm({...faqForm, category: e.target.value})}
              className="rounded-xl"
            />
            <Button onClick={createFAQ} className="w-full rounded-xl">
              Add FAQ
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk FAQ Upload Dialog */}
      <Dialog open={showBulkFAQ} onOpenChange={setShowBulkFAQ}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Upload FAQs</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">Paste your FAQ list below. Use this format:</p>
            <div className="p-3 rounded-xl bg-gray-50 border border-gray-200 text-xs text-gray-600 font-mono whitespace-pre-wrap">
{`Q: What time does the program start?
A: 9:00 AM Monday through Friday.

Q: How do I get paid?
A: Bi-weekly via direct deposit or paycard.

Q: Who is my supervisor?
A: Your Lead Monitor assigned to your cohort.`}
            </div>
            <Textarea
              value={bulkFAQText}
              onChange={e => setBulkFAQText(e.target.value)}
              placeholder="Paste your Q&A pairs here..."
              rows={8}
              className="rounded-xl font-mono text-sm"
              data-testid="bulk-faq-text"
            />
            <Button onClick={async () => {
              if (!bulkFAQText.trim()) { toast.error('Paste your FAQ content'); return; }
              try {
                const res = await fetch(`${API_URL}/api/faq/bulk-upload`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ text: bulkFAQText })
                });
                if (res.ok) {
                  const d = await res.json();
                  toast.success(`${d.created} FAQs added!`);
                  setShowBulkFAQ(false);
                  setBulkFAQText('');
                  fetchAllData();
                }
              } catch (e) { toast.error('Upload failed'); }
            }} className="w-full rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold" data-testid="bulk-faq-submit">
              Upload FAQs
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== PARENTS TAB ====================
const ParentsTab = ({ users }) => {
  const [parentMessages, setParentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const parents = (users || []).filter(u => u.role === 'parent');
  const youth = (users || []).filter(u => u.role === 'youth');
  const youthMap = {};
  youth.forEach(y => { youthMap[y.id] = y.name; });

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/parent/messages`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setParentMessages(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchMessages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUnansweredCount = (parentId) => {
    const fromParent = parentMessages.filter(m => m.from_id === parentId && m.from_role === 'parent');
    const replies = parentMessages.filter(m => m.from_role === 'lead_monitor' || m.from_role === 'monitor');
    // Simple heuristic: if parent's last message is newer than last reply, it's unanswered
    const lastParentMsg = fromParent.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0];
    const lastReply = replies.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0];
    if (lastParentMsg && (!lastReply || (lastParentMsg.created_at || '') > (lastReply.created_at || ''))) return 1;
    return 0;
  };

  return (
    <Card className="p-5 rounded-2xl">
      <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><Heart className="w-4 h-4" /> Parent Engagement</h3>
      {parents.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">No parents enrolled yet</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left">
              <th className="p-2 font-semibold">Parent</th>
              <th className="p-2 font-semibold">Linked Youth</th>
              <th className="p-2 font-semibold text-center">Consent</th>
              <th className="p-2 font-semibold text-center">Messages</th>
              <th className="p-2 font-semibold text-center">Unanswered</th>
              <th className="p-2 font-semibold text-center">Last Active</th>
            </tr></thead>
            <tbody>
              {parents.map(p => {
                const linkedYouth = youth.find(y => y.id === p.linked_youth_id);
                const linkedYouthName = linkedYouth?.name || youthMap[p.linked_youth_id] || 'Not linked';
                const unanswered = getUnansweredCount(p.id);
                const msgCount = parentMessages.filter(m => m.from_id === p.id).length;
                const lastLogin = p.last_login || p.created_at || '';
                return (
                  <React.Fragment key={p.id}>
                  <tr className={`border-b hover:bg-gray-50 cursor-pointer ${unanswered > 0 ? 'bg-red-50' : ''}`}
                    onClick={() => setExpanded(expanded === p.id ? null : p.id)}>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{p.avatar_emoji || '👤'}</span>
                        <div><p className="font-semibold">{p.name}</p><p className="text-[10px] text-muted-foreground">{p.email}</p>{p.phone && <p className="text-[10px] text-muted-foreground">{p.phone}</p>}</div>
                      </div>
                    </td>
                    <td className="p-2 text-sm font-medium">{linkedYouthName}</td>
                    <td className="p-2 text-center">
                      <Badge className={p.parent_consent_completed ? 'bg-emerald-100 text-emerald-800 text-[10px]' : 'bg-amber-100 text-amber-800 text-[10px]'}>
                        {p.parent_consent_completed ? 'Signed' : 'Pending'}
                      </Badge>
                    </td>
                    <td className="p-2 text-center">{msgCount}</td>
                    <td className="p-2 text-center">
                      {unanswered > 0 ? <Badge className="bg-red-100 text-red-800 text-[10px]">{unanswered} unanswered</Badge> : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="p-2 text-center text-xs text-muted-foreground">{lastLogin ? new Date(lastLogin).toLocaleDateString() : 'Never'}</td>
                  </tr>
                  {expanded === p.id && linkedYouth && (
                    <tr><td colSpan={6} className="p-3 bg-blue-50">
                      <div className="flex items-center gap-4">
                        <span className="text-2xl">{linkedYouth.avatar_emoji || '😊'}</span>
                        <div>
                          <p className="font-bold text-sm">{linkedYouth.name}</p>
                          <p className="text-xs text-gray-500">{linkedYouth.email}</p>
                          {linkedYouth.work_site && <p className="text-xs text-gray-500">Work Site: {linkedYouth.work_site}</p>}
                          {linkedYouth.school && <p className="text-xs text-gray-500">School: {linkedYouth.school}</p>}
                          <p className="text-xs text-gray-500">Points: {linkedYouth.total_points || 0} | Lead Monitor: {linkedYouth.lead_monitor_name || 'Unassigned'}</p>
                        </div>
                      </div>
                    </td></tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

// ==================== STAFF TAB ====================
const StaffTab = ({ users }) => {
  const staff = (users || []).filter(u => u.role === 'lead_monitor' || u.role === 'coach');
  const youth = (users || []).filter(u => u.role === 'youth');
  const [expanded, setExpanded] = useState(null);
  const [responseTimes, setResponseTimes] = useState(null);

  useEffect(() => {
    const fetchResponseTimes = async () => {
      try {
        const res = await fetch(`${API_URL}/api/analytics/monitor-response-times`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) setResponseTimes(await res.json());
      } catch (e) { console.error(e); }
    };
    fetchResponseTimes();
  }, []);

  const getMonitorStats = (monitorId) => {
    if (!responseTimes) return null;
    return responseTimes.monitors?.find(m => m.monitor_id === monitorId);
  };

  return (
    <div className="space-y-4">
      {/* Response Time Overview */}
      {responseTimes && (
        <Card className="p-5 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50">
          <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-blue-500" /> Q&A Response Overview
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{responseTimes.total_pending_all}</p>
              <p className="text-xs text-gray-500">Pending Questions</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{responseTimes.total_answered_all}</p>
              <p className="text-xs text-gray-500">Total Answered</p>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-5 rounded-2xl">
        <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><Shield className="w-4 h-4" /> Staff Performance</h3>
        {staff.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">No staff members found</p>
        ) : (
          <div className="space-y-3">
            {staff.map(s => {
              const assignedYouth = youth.filter(y => y.lead_monitor_id === s.id);
              const lastLogin = s.last_login || s.created_at || '';
              const daysAgo = lastLogin ? Math.floor((Date.now() - new Date(lastLogin).getTime()) / 86400000) : 999;
              const isExpanded = expanded === s.id;
              const monitorStats = getMonitorStats(s.id);
              return (
                <Card key={s.id} className={`rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-md ${isExpanded ? 'ring-1 ring-orange-300' : ''}`}
                  onClick={() => setExpanded(isExpanded ? null : s.id)}>
                  <div className="p-4 flex items-center gap-3">
                    <span className="text-2xl">{s.avatar_emoji || '👤'}</span>
                    <div className="flex-1">
                      <p className="font-bold text-sm">{s.name}</p>
                      <p className="text-[10px] text-muted-foreground">{s.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">{assignedYouth.length} youth</p>
                      <Badge className={daysAgo <= 7 ? 'bg-emerald-100 text-emerald-800 text-[9px]' : 'bg-red-100 text-red-800 text-[9px]'}>
                        {daysAgo <= 7 ? 'Active' : `${daysAgo}d inactive`}
                      </Badge>
                    </div>
                  </div>
                  {/* Response Stats Bar */}
                  {monitorStats && (
                    <div className="px-4 pb-2 flex items-center gap-3 text-[10px]">
                      <span className="text-amber-600 font-medium">{monitorStats.pending} pending</span>
                      <span className="text-green-600">{monitorStats.answered} answered</span>
                      <span className="text-gray-500">Avg: {monitorStats.avg_response_display}</span>
                      <span className={`ml-auto font-bold ${monitorStats.response_rate >= 80 ? 'text-green-600' : monitorStats.response_rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                        {monitorStats.response_rate}% response rate
                      </span>
                    </div>
                  )}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                      <p className="text-xs font-bold text-gray-500 mb-2">Assigned Youth ({assignedYouth.length}):</p>
                      <div className="grid grid-cols-2 gap-2">
                        {assignedYouth.map(y => (
                          <div key={y.id} className="p-2 rounded-lg bg-gray-50 flex items-center gap-2">
                            <span>{y.avatar_emoji || '😊'}</span>
                            <div><p className="text-xs font-medium">{y.name}</p><p className="text-[9px] text-gray-400">{y.work_site || y.school || ''}</p></div>
                          </div>
                        ))}
                      </div>
                      {assignedYouth.length === 0 && <p className="text-xs text-gray-400">No youth assigned yet</p>}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

// ==================== YOUTH/USERS TAB ====================
const YouthUsersTab = ({ users, allUsers }) => {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userReport, setUserReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', role: 'youth', work_site: '', cohort: '' });

  const staffMap = {};
  (allUsers || []).filter(u => u.role === 'lead_monitor' || u.role === 'coach').forEach(s => { staffMap[s.id] = s.name; });

  // Group counts for NextGEN/UpNEXT filter
  const nextgenCount = (users || []).filter(u => u.role === 'youth' && u.program_type === 'nextgen').length;
  const upnextCount = (users || []).filter(u => u.role === 'youth' && u.program_type === 'upnext').length;

  const filtered = (users || []).filter(u => {
    if (u.role === 'platform_admin') return false;
    const matchSearch = u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'all' || u.role === roleFilter || 
      (roleFilter === 'inactive' && u.role === 'youth' && (!u.last_activity || u.total_points === 0)) ||
      (roleFilter === 'no_goals' && u.role === 'youth');
    const matchGroup = groupFilter === 'all' || u.program_type === groupFilter;
    if (roleFilter === 'inactive') return matchSearch && u.role === 'youth' && (!u.last_activity || u.total_points === 0) && matchGroup;
    if (roleFilter === 'no_goals') return matchSearch && u.role === 'youth' && (!u.goals_count || u.goals_count === 0) && matchGroup;
    return matchSearch && matchRole && matchGroup;
  });

  const inactiveCount = (users || []).filter(u => u.role === 'youth' && (!u.last_activity || u.total_points === 0)).length;

  const openProfile = async (userId) => {
    setSelectedUser(userId);
    setLoadingReport(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/gps/student-report/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setUserReport(await res.json());
    } catch (e) { console.error(e); }
    setLoadingReport(false);
  };

  if (selectedUser && userReport) {
    const s = userReport.student || {};
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => { setSelectedUser(null); setUserReport(null); }} className="rounded-xl">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Users
        </Button>
        <Card className="p-5 rounded-2xl">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center text-2xl">{s.avatar_emoji || '😊'}</div>
            <div className="flex-1">
              <h2 className="text-xl font-bold">{s.name}</h2>
              <p className="text-sm text-muted-foreground">{s.email} {s.grade ? `| Grade ${s.grade}` : ''} {s.work_site ? `| ${s.work_site}` : ''}</p>
              <p className="text-xs text-muted-foreground">Points: {s.total_points} | Streak: {s.current_streak} days | Lead Monitor: {staffMap[s.lead_monitor_id] || 'Unassigned'}</p>
            </div>
          </div>
          {userReport.ai_summary && (
            <div className="p-4 rounded-xl bg-violet-50 border border-violet-200 mb-4">
              <p className="text-xs font-bold text-violet-800 mb-1">AI Summary</p>
              <p className="text-sm text-violet-900">{userReport.ai_summary}</p>
            </div>
          )}
        </Card>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="p-3 rounded-xl bg-emerald-50"><p className="text-xl font-bold text-emerald-700">{userReport.goals?.completed || 0}/{userReport.goals?.total || 0}</p><p className="text-xs text-emerald-600">Goals</p></Card>
          <Card className="p-3 rounded-xl bg-violet-50"><p className="text-xl font-bold text-violet-700">{userReport.development?.workshops_completed || 0}</p><p className="text-xs text-violet-600">Courses</p></Card>
          <Card className="p-3 rounded-xl bg-blue-50"><p className="text-xl font-bold text-blue-700">{userReport.career_exploration?.total || 0}</p><p className="text-xs text-blue-600">Careers</p></Card>
          <Card className="p-3 rounded-xl bg-amber-50"><p className="text-xl font-bold text-amber-700">{userReport.development?.checkins_completed || 0}</p><p className="text-xs text-amber-600">Check-ins</p></Card>
        </div>
        {userReport.identity?.interests?.length > 0 && (
          <Card className="p-4 rounded-2xl"><h3 className="font-bold text-sm mb-2">Interests</h3><div className="flex flex-wrap gap-1">{userReport.identity.interests.map((i, idx) => <Badge key={idx} className="text-xs bg-pink-50 text-pink-700">{i}</Badge>)}</div></Card>
        )}
        {userReport.goals?.active_goals?.length > 0 && (
          <Card className="p-4 rounded-2xl"><h3 className="font-bold text-sm mb-2">Active Goals</h3>{userReport.goals.active_goals.map((g, i) => <div key={i} className="p-2 rounded-lg bg-gray-50 mb-1 text-sm">{g.title} <span className="text-xs text-gray-400">{g.deadline ? `due ${new Date(g.deadline).toLocaleDateString()}` : ''}</span></div>)}</Card>
        )}
        {userReport.career_exploration?.careers?.length > 0 && (
          <Card className="p-4 rounded-2xl"><h3 className="font-bold text-sm mb-2">Careers Exploring</h3>{userReport.career_exploration.careers.map((c, i) => <div key={i} className="p-2 rounded-lg bg-violet-50 mb-1 text-sm">{c.title} <span className="text-xs text-gray-400">{c.salary}</span></div>)}</Card>
        )}
        {userReport.development?.workshop_details?.length > 0 && (
          <Card className="p-4 rounded-2xl"><h3 className="font-bold text-sm mb-2">Courses Completed</h3>{userReport.development.workshop_details.map((w, i) => <div key={i} className="p-2 rounded-lg bg-emerald-50 mb-1 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /><span className="text-sm">{w.title}</span></div>)}</Card>
        )}
        {userReport.counselor_notes?.length > 0 && (
          <Card className="p-4 rounded-2xl"><h3 className="font-bold text-sm mb-2">Staff Notes</h3>{userReport.counselor_notes.map((n, i) => <div key={i} className="p-2 rounded-lg bg-gray-50 mb-1"><Badge className="text-[9px] mr-2">{n.category}</Badge><span className="text-sm">{n.note}</span></div>)}</Card>
        )}
      </div>
    );
  }

  return (
    <Card className="p-5 rounded-2xl">
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-xl" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[160px] rounded-xl"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="youth">Youth</SelectItem>
            <SelectItem value="inactive">Inactive Youth ({inactiveCount})</SelectItem>
            <SelectItem value="lead_monitor">Lead Monitor</SelectItem>
            <SelectItem value="parent">Parent</SelectItem>
            <SelectItem value="coach">Coach</SelectItem>
          </SelectContent>
        </Select>
        <Select value={groupFilter} onValueChange={setGroupFilter}>
          <SelectTrigger className="w-[180px] rounded-xl"><SelectValue placeholder="Group" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Groups</SelectItem>
            <SelectItem value="nextgen">NextGEN ({nextgenCount})</SelectItem>
            <SelectItem value="upnext">UpNEXT ({upnextCount})</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setShowAddUser(true)} className="rounded-xl bg-orange-500 hover:bg-orange-600 text-white" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-1" /> Add User
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left">
            <th className="p-2 font-semibold">Name</th>
            <th className="p-2 font-semibold">Role</th>
            <th className="p-2 font-semibold">Lead Monitor</th>
            <th className="p-2 font-semibold text-center">Points</th>
            <th className="p-2 font-semibold text-center">Status</th>
            <th className="p-2 font-semibold text-center">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.slice(0, 50).map(u => {
              const lastLogin = u.last_login || u.created_at || '';
              const daysAgo = lastLogin ? Math.floor((Date.now() - new Date(lastLogin).getTime()) / 86400000) : 999;
              const statusColor = daysAgo <= 7 ? 'bg-emerald-100 text-emerald-800' : daysAgo <= 14 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';
              const statusText = daysAgo <= 7 ? 'Active' : daysAgo <= 14 ? `${daysAgo}d ago` : daysAgo < 999 ? `${daysAgo}d ago` : 'Never';
              return (
                <tr key={u.id} className={`border-b transition-colors ${u.role === 'youth' ? 'hover:bg-blue-50 cursor-pointer' : 'hover:bg-gray-50'}`}>
                  <td className="p-2" onClick={() => u.role === 'youth' && openProfile(u.id)}>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{u.avatar_emoji || '😊'}</span>
                      <div><p className="font-semibold">{u.name}</p><p className="text-[10px] text-muted-foreground">{u.email}</p></div>
                    </div>
                  </td>
                  <td className="p-2"><Badge variant="outline" className="capitalize text-[10px]">{u.role?.replace('_', ' ')}</Badge></td>
                  <td className="p-2 text-xs text-muted-foreground">{staffMap[u.lead_monitor_id] || (u.role === 'youth' ? 'Unassigned' : '—')}</td>
                  <td className="p-2 text-center font-bold">{u.total_points || 0}</td>
                  <td className="p-2 text-center"><Badge className={`text-[10px] ${statusColor}`}>{statusText}</Badge></td>
                  <td className="p-2 text-center">
                    {u.role === 'youth' && u.status !== 'suspended' && (
                      <button onClick={async (e) => { e.stopPropagation(); if (window.confirm(`Suspend ${u.name}? They will not be able to log in.`)) {
                        try { await fetch(`${API_URL}/api/admin/remove-user/${u.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}); toast.success('User suspended'); window.location.reload(); } catch(err) { toast.error('Failed'); }
                      }}} className="text-[10px] text-red-500 hover:text-red-700 font-bold">Suspend</button>
                    )}
                    {u.status === 'suspended' && <span className="text-[10px] text-gray-400">Suspended</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {loadingReport && <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}
      
      {/* Add Individual User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle>Add Individual User</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><Label className="text-xs font-bold">Full Name *</Label><Input value={addForm.name} onChange={e => setAddForm({...addForm, name: e.target.value})} placeholder="First Last" className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs font-bold">Email *</Label><Input value={addForm.email} onChange={e => setAddForm({...addForm, email: e.target.value})} placeholder="email@example.com" className="mt-1 rounded-xl" type="email" /></div>
            <div><Label className="text-xs font-bold">Role</Label>
              <select value={addForm.role} onChange={e => setAddForm({...addForm, role: e.target.value})} className="w-full p-2 rounded-xl border mt-1 text-sm">
                <option value="youth">Youth Participant</option>
                <option value="lead_monitor">Lead Monitor / Mentor</option>
                <option value="coach">Coach / Counselor</option>
              </select>
            </div>
            {addForm.role === 'youth' && (
              <>
                <div><Label className="text-xs font-bold">Work Site</Label><Input value={addForm.work_site} onChange={e => setAddForm({...addForm, work_site: e.target.value})} placeholder="e.g. City Hall" className="mt-1 rounded-xl" /></div>
                <div><Label className="text-xs font-bold">Cohort</Label><Input value={addForm.cohort} onChange={e => setAddForm({...addForm, cohort: e.target.value})} placeholder="Cohort A" className="mt-1 rounded-xl" /></div>
              </>
            )}
            <Button onClick={async () => {
              if (!addForm.name || !addForm.email) { toast.error('Name and email required'); return; }
              try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/api/auth/register`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ name: addForm.name, email: addForm.email, password: 'Welcome2026!', role: addForm.role, work_site: addForm.work_site })
                });
                if (res.ok) { toast.success(`${addForm.name} added! Temp password: Welcome2026!`); setShowAddUser(false); setAddForm({ name: '', email: '', role: 'youth', work_site: '', cohort: '' }); window.location.reload(); }
                else { const d = await res.json(); toast.error(d.detail || 'Failed to add user'); }
              } catch (e) { toast.error('Failed'); }
            }} className="w-full rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold" data-testid="add-user-submit">
              Add User & Send Welcome Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ==================== PROGRAM REPORTS TAB ====================
const ProgramReportsTab = ({ user }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYouth, setSelectedYouth] = useState(null);
  const [youthReport, setYouthReport] = useState(null);
  const [loadingYouth, setLoadingYouth] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchReport(); }, []);

  const fetchReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/gps/program-report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setReport(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const exportCSV = () => {
    if (!report?.youth_summaries) return;
    const headers = ['Name', 'Email', 'Points', 'Goals Completed', 'Goals Total', 'Courses Completed', 'Check-ins', 'Careers Explored'];
    const rows = report.youth_summaries.map(y => [y.name, y.email, y.points, y.goals_completed, y.goals_total, y.workshops_completed, y.checkins, y.careers_explored]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `yas_connect_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  const openYouthReport = async (youthName) => {
    setLoadingYouth(true);
    try {
      const token = localStorage.getItem('token');
      // Find youth ID from users
      const usersRes = await fetch(`${API_URL}/api/admin/users?limit=1200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (usersRes.ok) {
        const userData = await usersRes.json();
        const youth = (userData.users || []).find(u => u.name === youthName && u.role === 'youth');
        if (youth) {
          const repRes = await fetch(`${API_URL}/api/gps/student-report/${youth.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (repRes.ok) {
            setYouthReport(await repRes.json());
            setSelectedYouth(youthName);
          }
        }
      }
    } catch (e) { console.error(e); }
    setLoadingYouth(false);
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>;
  if (!report) return <p className="text-center py-12 text-gray-400">No data available</p>;

  const a = report.aggregate || {};

  // Youth report slide-out
  if (selectedYouth && youthReport) {
    const s = youthReport.student || {};
    return (
      <div className="space-y-4">
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setSelectedYouth(null); setYouthReport(null); }} className="mb-2">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Reports
          </Button>
          <Button 
            variant="default" 
            className="mb-2 bg-orange-500 hover:bg-orange-600 rounded-xl text-xs"
            data-testid="download-youth-pdf"
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const usersRes = await fetch(`${API_URL}/api/admin/users?limit=1200`, { headers: { Authorization: `Bearer ${token}` } });
                if (usersRes.ok) {
                  const userData = await usersRes.json();
                  const youth = (userData.users || []).find(u => u.name === selectedYouth && u.role === 'youth');
                  if (youth) {
                    const res = await fetch(`${API_URL}/api/reports/pdf/youth-detail/${youth.id}`, { headers: { Authorization: `Bearer ${token}` } });
                    if (res.ok) {
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = `${selectedYouth.replace(/ /g, '_')}_report.pdf`;
                      a.click(); URL.revokeObjectURL(url);
                      toast.success('Youth PDF downloaded!');
                    }
                  }
                }
              } catch (e) { toast.error('Failed to generate PDF'); }
            }}
          >
            <FileDown className="w-3 h-3 mr-1" /> Download Youth PDF
          </Button>
        </div>
        <Card className="p-5 rounded-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center text-2xl">{s.avatar_emoji || '🎓'}</div>
            <div>
              <h2 className="text-xl font-bold">{s.name}</h2>
              <p className="text-sm text-muted-foreground">{s.email} {s.grade ? `| Grade ${s.grade}` : ''} {s.work_site ? `| ${s.work_site}` : ''}</p>
              <p className="text-xs text-muted-foreground">Points: {s.total_points} | Streak: {s.current_streak} days</p>
            </div>
          </div>
          {youthReport.ai_summary && (
            <div className="p-4 rounded-xl bg-violet-50 border border-violet-200 mb-4">
              <p className="text-xs font-bold text-violet-800 mb-1">AI Summary</p>
              <p className="text-sm text-violet-900">{youthReport.ai_summary}</p>
            </div>
          )}
        </Card>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Goals', val: `${youthReport.goals?.completed || 0}/${youthReport.goals?.total || 0}`, sub: 'completed', color: 'emerald' },
            { label: 'Courses', val: youthReport.development?.workshops_completed || 0, sub: 'completed', color: 'violet' },
            { label: 'Careers', val: youthReport.career_exploration?.total || 0, sub: 'explored', color: 'blue' },
            { label: 'Check-ins', val: youthReport.development?.checkins_completed || 0, sub: 'submitted', color: 'amber' },
          ].map(s => (
            <Card key={s.label} className={`p-4 rounded-xl bg-${s.color}-50`}>
              <p className={`text-2xl font-bold text-${s.color}-700`}>{s.val}</p>
              <p className={`text-xs text-${s.color}-600`}>{s.label} {s.sub}</p>
            </Card>
          ))}
        </div>

        {youthReport.identity?.interests?.length > 0 && (
          <Card className="p-4 rounded-2xl">
            <h3 className="font-bold text-sm mb-2">Interests</h3>
            <div className="flex flex-wrap gap-1">{youthReport.identity.interests.map((i, idx) => <Badge key={idx} className="text-xs bg-pink-50 text-pink-700">{i}</Badge>)}</div>
          </Card>
        )}

        {youthReport.goals?.active_goals?.length > 0 && (
          <Card className="p-4 rounded-2xl">
            <h3 className="font-bold text-sm mb-2">Active Goals</h3>
            {youthReport.goals.active_goals.map((g, i) => (
              <div key={i} className="p-2 rounded-lg bg-gray-50 mb-1 text-sm">{g.title} <span className="text-xs text-gray-400">{g.deadline ? `due ${new Date(g.deadline).toLocaleDateString()}` : ''}</span></div>
            ))}
          </Card>
        )}

        {youthReport.career_exploration?.careers?.length > 0 && (
          <Card className="p-4 rounded-2xl">
            <h3 className="font-bold text-sm mb-2">Careers Exploring</h3>
            {youthReport.career_exploration.careers.map((c, i) => (
              <div key={i} className="p-2 rounded-lg bg-violet-50 mb-1 text-sm">{c.title} <span className="text-xs text-gray-400">{c.salary} | {c.education}</span></div>
            ))}
          </Card>
        )}

        {youthReport.development?.workshop_details?.length > 0 && (
          <Card className="p-4 rounded-2xl">
            <h3 className="font-bold text-sm mb-2">Completed Courses</h3>
            {youthReport.development.workshop_details.map((w, i) => (
              <div key={i} className="p-2 rounded-lg bg-emerald-50 mb-1 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-sm">{w.title}</span>
                <span className="text-xs text-gray-400 ml-auto">{w.completed_at ? new Date(w.completed_at).toLocaleDateString() : ''}</span>
              </div>
            ))}
          </Card>
        )}

        {youthReport.counselor_notes?.length > 0 && (
          <Card className="p-4 rounded-2xl">
            <h3 className="font-bold text-sm mb-2">Staff Notes</h3>
            {youthReport.counselor_notes.map((n, i) => (
              <div key={i} className="p-2 rounded-lg bg-gray-50 mb-1">
                <div className="flex items-center gap-2 mb-1"><Badge className="text-[9px]">{n.category}</Badge><span className="text-[10px] text-gray-400">{n.date ? new Date(n.date).toLocaleDateString() : ''}</span></div>
                <p className="text-sm">{n.note}</p>
              </div>
            ))}
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Visual Analytics Banner */}
      <div 
        className="relative overflow-hidden rounded-2xl p-5 cursor-pointer hover:shadow-lg transition-all"
        style={{ background: 'linear-gradient(135deg, #1B2D4F 0%, #1E6B7B 100%)' }}
        onClick={() => window.location.href = '/admin/analytics'}
        data-testid="open-analytics-btn"
      >
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4 text-white">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Visual Analytics Dashboard</h3>
              <p className="text-white/70 text-sm">Interactive KPI dashboards for Courses, Projects, Events, Q&A, Staff, Worksites & more</p>
            </div>
          </div>
          <ChevronLeft className="w-5 h-5 text-white/60 rotate-180" />
        </div>
      </div>

      {/* Assessment Builder Banner */}
      <div 
        className="relative overflow-hidden rounded-2xl p-4 cursor-pointer hover:shadow-lg transition-all"
        style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }}
        onClick={() => window.location.href = '/admin/assessment-builder'}
        data-testid="open-assessment-builder-btn"
      >
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold">AI Assessment Builder</h3>
              <p className="text-white/70 text-xs">Create custom assessments with AI assistance</p>
            </div>
          </div>
          <ChevronLeft className="w-5 h-5 text-white/60 rotate-180" />
        </div>
      </div>

      {/* Export bar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-bold">Program Analytics</h3>
        <div className="flex gap-2">
          <Button 
            variant="default" 
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/api/reports/pdf/program-overview`, { headers: { Authorization: `Bearer ${token}` } });
                if (res.ok) {
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `program_overview_${new Date().toISOString().split('T')[0]}.pdf`;
                  a.click(); URL.revokeObjectURL(url);
                  toast.success('PDF downloaded!');
                }
              } catch (e) { toast.error('Failed to generate PDF'); }
            }} 
            className="rounded-xl text-xs bg-orange-500 hover:bg-orange-600" 
            data-testid="export-pdf-btn"
          >
            <FileDown className="w-3 h-3 mr-1" /> Download PDF
          </Button>
          <Button 
            variant="default"
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/api/reports/pdf/funder-summary`, { headers: { Authorization: `Bearer ${token}` } });
                if (res.ok) {
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `funder_summary_${new Date().toISOString().split('T')[0]}.pdf`;
                  a.click(); URL.revokeObjectURL(url);
                  toast.success('Funder summary downloaded!');
                }
              } catch (e) { toast.error('Failed to generate PDF'); }
            }}
            className="rounded-xl text-xs bg-violet-500 hover:bg-violet-600"
            data-testid="export-funder-pdf-btn"
          >
            <FileDown className="w-3 h-3 mr-1" /> Funder Summary
          </Button>
          <Button variant="outline" onClick={exportCSV} className="rounded-xl text-xs" data-testid="export-csv-btn">
            <FileText className="w-3 h-3 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total Youth', val: report.total_youth, icon: Users, color: 'blue' },
          { label: 'Goals Completed', val: `${a.completed_goals}/${a.total_goals}`, icon: Target, color: 'emerald' },
          { label: 'Courses Done', val: a.total_workshops_completed, icon: BookOpen, color: 'violet' },
          { label: 'Careers Explored', val: a.total_careers_explored, icon: Briefcase, color: 'amber' },
          { label: 'Check-ins', val: a.total_checkins, icon: CheckCircle, color: 'green' },
          { label: 'Wins Posted', val: a.total_wins, icon: Trophy, color: 'orange' },
          { label: 'Avg Points', val: a.avg_points, icon: Star, color: 'yellow' },
          { label: 'Goal Rate', val: `${a.goal_completion_rate}%`, icon: TrendingUp, color: 'pink' },
        ].map(kpi => (
          <Card key={kpi.label} className={`p-4 rounded-xl`}>
            <kpi.icon className={`w-5 h-5 text-${kpi.color}-500 mb-2`} />
            <p className="text-2xl font-bold">{kpi.val}</p>
            <p className="text-xs text-muted-foreground">{kpi.label}</p>
          </Card>
        ))}
      </div>

      {/* Consent Summary */}
      <Card className="p-4 rounded-2xl">
        <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><Shield className="w-4 h-4" /> Parent Consent</h3>
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 bg-emerald-50 rounded-xl text-center">
            <p className="text-2xl font-bold text-emerald-700">{report.consent?.consented || 0}</p>
            <p className="text-xs text-emerald-600">Signed</p>
          </div>
          <div className="p-3 bg-amber-50 rounded-xl text-center">
            <p className="text-2xl font-bold text-amber-700">{report.consent?.pending || 0}</p>
            <p className="text-xs text-amber-600">Pending</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-xl text-center">
            <p className="text-2xl font-bold text-gray-700">{report.consent?.total || 0}</p>
            <p className="text-xs text-gray-600">Total Requests</p>
          </div>
        </div>
      </Card>

      {/* Youth Table with click-to-report */}
      <Card className="p-4 rounded-2xl">
        <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Youth Performance (click for full report)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="p-2 font-semibold">Youth</th>
                <th className="p-2 font-semibold text-center">Points</th>
                <th className="p-2 font-semibold text-center">Goals</th>
                <th className="p-2 font-semibold text-center">Courses</th>
                <th className="p-2 font-semibold text-center">Check-ins</th>
                <th className="p-2 font-semibold text-center">Careers</th>
              </tr>
            </thead>
            <tbody>
              {(report.youth_summaries || []).map((y, i) => (
                <tr key={i} onClick={() => openYouthReport(y.name)}
                  className="border-b hover:bg-blue-50 cursor-pointer transition-colors" data-testid={`youth-row-${i}`}>
                  <td className="p-2">
                    <p className="font-semibold">{y.name}</p>
                    <p className="text-xs text-muted-foreground">{y.email}</p>
                  </td>
                  <td className="p-2 text-center font-bold">{y.points}</td>
                  <td className="p-2 text-center">
                    <span className="text-emerald-600 font-semibold">{y.goals_completed}</span>
                    <span className="text-gray-400">/{y.goals_total}</span>
                  </td>
                  <td className="p-2 text-center">{y.workshops_completed}</td>
                  <td className="p-2 text-center">{y.checkins}</td>
                  <td className="p-2 text-center">{y.careers_explored}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ==================== PROGRAM SETTINGS SECTION ====================
const ProgramSettingsSection = ({ user, fetchAllData }) => {
  const [program, setProgram] = useState(null);
  const [org, setOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ display_name: '', description: '', start_date: '', end_date: '', location: '', cohorts: '' });
  const [orgForm, setOrgForm] = useState({ name: '', phone: '', website: '', address: '', description: '' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await window.fetch(`${API_URL}/api/platform/my-program`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const d = await res.json();
          if (d) {
            setProgram(d);
            setForm({
              display_name: d.display_name || '',
              description: d.description || '',
              start_date: d.start_date || '',
              end_date: d.end_date || '',
              location: d.location || '',
              cohorts: (d.cohorts || []).join(', ')
            });
            // Fetch org
            if (d.organization_id) {
              const orgRes = await window.fetch(`${API_URL}/api/platform/organizations/${d.organization_id}`, { headers: { Authorization: `Bearer ${token}` } });
              if (orgRes.ok) {
                const orgData = await orgRes.json();
                setOrg(orgData);
                // Handle address - it may be an object or string
                let addressStr = '';
                if (orgData.address) {
                  if (typeof orgData.address === 'object') {
                    const a = orgData.address;
                    addressStr = [a.street, a.city, a.state, a.zip_code].filter(Boolean).join(', ');
                  } else {
                    addressStr = orgData.address;
                  }
                }
                setOrgForm({
                  name: orgData.name || '',
                  phone: orgData.phone || '',
                  website: orgData.website || '',
                  address: addressStr,
                  description: orgData.description || ''
                });
              }
            }
          }
        }
      } catch (e) { /* ignore */ }
    };
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSettings = async () => {
    if (!program) return;
    setSaving(true);
    try {
      // Save program settings
      await window.fetch(`${API_URL}/api/platform/programs/${program.id}`, {
        method: 'PUT', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: form.display_name, description: form.description, start_date: form.start_date, end_date: form.end_date, location: form.location, cohorts: form.cohorts.split(',').map(s => s.trim()).filter(Boolean) })
      });
      // Save org settings if org exists
      if (org) {
        await window.fetch(`${API_URL}/api/platform/organizations/${org.id}`, {
          method: 'PUT', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(orgForm)
        });
      }
      toast.success('Settings saved!');
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <Card className="p-6 rounded-2xl">
      <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><Settings className="w-4 h-4 text-orange-500" /> Program & Organization Settings</h3>
      <p className="text-xs text-gray-400 mb-4">Edit your program and organization details.</p>
      
      {/* Organization Info */}
      {org && (
        <div className="mb-6 p-4 rounded-xl bg-blue-50 border border-blue-200">
          <h4 className="font-bold text-xs text-blue-800 mb-3">Organization Info</h4>
          <div className="space-y-2">
            <div><Label className="text-xs font-bold text-blue-700">Organization Name</Label><Input value={orgForm.name} onChange={e => setOrgForm({...orgForm, name: e.target.value})} className="mt-1 rounded-xl bg-white" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold text-blue-700">Phone</Label><Input value={orgForm.phone} onChange={e => setOrgForm({...orgForm, phone: e.target.value})} placeholder="(555) 123-4567" className="mt-1 rounded-xl bg-white" /></div>
              <div><Label className="text-xs font-bold text-blue-700">Website</Label><Input value={orgForm.website} onChange={e => setOrgForm({...orgForm, website: e.target.value})} placeholder="https://..." className="mt-1 rounded-xl bg-white" /></div>
            </div>
            <div><Label className="text-xs font-bold text-blue-700">Address</Label><Input value={orgForm.address} onChange={e => setOrgForm({...orgForm, address: e.target.value})} className="mt-1 rounded-xl bg-white" /></div>
          </div>
        </div>
      )}
      
      {/* Program Info */}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs font-bold">Program Name</Label><Input value={form.display_name} onChange={e => setForm({...form, display_name: e.target.value})} className="mt-1 rounded-xl" /></div>
          <div><Label className="text-xs font-bold">Location</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Newark, NJ" className="mt-1 rounded-xl" /></div>
        </div>
        <div><Label className="text-xs font-bold">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} className="mt-1 rounded-xl" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs font-bold">Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="mt-1 rounded-xl" /></div>
          <div><Label className="text-xs font-bold">End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="mt-1 rounded-xl" /></div>
        </div>
        <div><Label className="text-xs font-bold">Cohorts / Groups (comma-separated)</Label><Input value={form.cohorts} onChange={e => setForm({...form, cohorts: e.target.value})} placeholder="Cohort A, Cohort B, Cohort C" className="mt-1 rounded-xl" /></div>
        <Button onClick={saveSettings} disabled={saving} className="rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold">
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>
    </Card>
  );
};

// ==================== SURVEY MANAGEMENT SECTION ====================
const SurveyManagementSection = ({ user }) => {
  const [surveys, setSurveys] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', survey_type: 'built_in', external_url: '', questions: [], is_required: false });
  const [newQ, setNewQ] = useState('');
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [results, setResults] = useState(null);

  useEffect(() => {
    fetchSurveys();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSurveys = async () => {
    try {
      const res = await window.fetch(`${API_URL}/api/surveys`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) setSurveys(await res.json());
    } catch (e) { /* ignore */ }
  };

  const createSurvey = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    if (form.survey_type === 'external' && !form.external_url) { toast.error('External URL required'); return; }
    if (form.survey_type === 'built_in' && form.questions.length === 0) { toast.error('Add at least one question'); return; }
    try {
      const res = await window.fetch(`${API_URL}/api/surveys`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: form.title, description: form.description, questions: form.survey_type === 'external' ? [{ question: 'External Survey', type: 'external', url: form.external_url }] : form.questions, is_required: form.is_required })
      });
      if (res.ok) { toast.success('Survey created! Youth notified.'); setShowCreate(false); setForm({ title: '', description: '', survey_type: 'built_in', external_url: '', questions: [], is_required: false }); fetchSurveys(); }
    } catch (e) { toast.error('Failed'); }
  };

  const viewResults = async (surveyId) => {
    try {
      const res = await window.fetch(`${API_URL}/api/surveys/${surveyId}/results`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { setResults(await res.json()); setSelectedSurvey(surveyId); }
    } catch (e) { toast.error('Failed to load results'); }
  };

  return (
    <Card className="p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-orange-500" /> Surveys</h3>
          <p className="text-xs text-gray-400">Create and manage surveys for your youth</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="rounded-xl text-xs bg-orange-500 hover:bg-orange-600" data-testid="create-survey-btn">
          <Plus className="w-3 h-3 mr-1" /> Create Survey
        </Button>
      </div>

      {/* Survey list */}
      <div className="space-y-2">
        {surveys.map(s => (
          <div key={s.id} className="p-3 rounded-xl bg-gray-50 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-sm font-bold">{s.title}</p>
              <p className="text-[10px] text-gray-400">{s.questions?.length || 0} questions · {s.is_required ? 'Required' : 'Optional'}</p>
            </div>
            <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => viewResults(s.id)} data-testid={`survey-results-${s.id}`}>Results</Button>
          </div>
        ))}
        {surveys.length === 0 && <p className="text-center text-gray-400 text-sm py-4">No surveys yet</p>}
      </div>

      {/* Create Survey Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-2xl max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Survey</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><Label className="text-xs font-bold">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Post-Orientation Feedback" className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs font-bold">Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Tell us about your experience" className="mt-1 rounded-xl" /></div>

            {/* Survey type toggle */}
            <div>
              <Label className="text-xs font-bold mb-2 block">Survey Type</Label>
              <div className="flex gap-2">
                <button onClick={() => setForm({...form, survey_type: 'built_in'})} className={`flex-1 p-3 rounded-xl text-xs font-bold border transition-all ${form.survey_type === 'built_in' ? 'bg-orange-50 border-orange-300 text-orange-700' : 'bg-white border-gray-200 text-gray-500'}`}>
                  Built-in Questions
                </button>
                <button onClick={() => setForm({...form, survey_type: 'external'})} className={`flex-1 p-3 rounded-xl text-xs font-bold border transition-all ${form.survey_type === 'external' ? 'bg-orange-50 border-orange-300 text-orange-700' : 'bg-white border-gray-200 text-gray-500'}`}>
                  External Link (Typeform, Google)
                </button>
              </div>
            </div>

            {form.survey_type === 'external' ? (
              <div><Label className="text-xs font-bold">Survey URL</Label><Input value={form.external_url} onChange={e => setForm({...form, external_url: e.target.value})} placeholder="https://forms.google.com/... or https://typeform.com/..." className="mt-1 rounded-xl" /></div>
            ) : (
              <div>
                <Label className="text-xs font-bold mb-2 block">Questions</Label>
                {form.questions.map((q, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-gray-500 bg-gray-100 rounded-lg px-2 py-1 flex-1">{q.question} ({q.type})</span>
                    <button onClick={() => setForm({...form, questions: form.questions.filter((_, idx) => idx !== i)})} className="text-red-400 text-xs">x</button>
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <Input value={newQ} onChange={e => setNewQ(e.target.value)} placeholder="Type a question" className="rounded-xl text-xs flex-1" />
                  <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => { if (newQ.trim()) { setForm({...form, questions: [...form.questions, { question: newQ, type: 'text' }]}); setNewQ(''); }}}>+ Text</Button>
                  <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => { if (newQ.trim()) { setForm({...form, questions: [...form.questions, { question: newQ, type: 'rating' }]}); setNewQ(''); }}}>+ Rating</Button>
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50">
              <input type="checkbox" checked={form.is_required} onChange={e => setForm({...form, is_required: e.target.checked})} className="w-4 h-4 accent-orange-500" />
              <div><p className="text-xs font-bold">Required</p><p className="text-[9px] text-gray-400">Youth must complete before using other features</p></div>
            </label>

            <Button onClick={createSurvey} className="w-full rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold">Create & Notify Youth</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      <Dialog open={!!results} onOpenChange={() => { setResults(null); setSelectedSurvey(null); }}>
        <DialogContent className="rounded-2xl max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Survey Results</DialogTitle></DialogHeader>
          {results && (
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-xl bg-emerald-50 text-center"><p className="text-xl font-bold text-emerald-700">{results.total_responses}</p><p className="text-[9px] text-emerald-600">Responses</p></div>
                <div className="p-3 rounded-xl bg-blue-50 text-center"><p className="text-xl font-bold text-blue-700">{results.total_possible}</p><p className="text-[9px] text-blue-600">Total Youth</p></div>
                <div className="p-3 rounded-xl bg-orange-50 text-center"><p className="text-xl font-bold text-orange-700">{results.completion_rate}%</p><p className="text-[9px] text-orange-600">Completed</p></div>
              </div>
              {results.responses?.length > 0 ? (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {results.responses.map((r, i) => (
                    <div key={i} className="p-2 rounded-lg bg-gray-50 text-xs">
                      {Object.entries(r.responses || {}).map(([k, v]) => (
                        <p key={k}><strong>{k}:</strong> {String(v)}</p>
                      ))}
                    </div>
                  ))}
                </div>
              ) : <p className="text-center text-gray-400 text-sm py-4">No responses yet</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminDashboard;
